'use client';

import { Castle, Lightbulb, Heart, Sparkles, Home, Users } from 'lucide-react';
import { motion } from 'framer-motion';
import { Button } from '../atomic/Button';

interface RattenbergEasterEggProps {
  onBack: () => void;
}

export function RattenbergEasterEgg({ onBack }: RattenbergEasterEggProps) {
  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50 p-4"
    >
      <div className="max-w-3xl mx-auto">
        {/* Header with Castle Icon */}
        <div className="text-center mb-8">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
            className="inline-flex items-center justify-center w-24 h-24 bg-amber-100 rounded-full mb-4"
          >
            <Castle className="w-12 h-12 text-amber-600" />
          </motion.div>
          
          <motion.h1 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="text-4xl font-bold text-gray-800 mb-2"
          >
            Willkommen in Rattenberg!
          </motion.h1>
          
          <motion.p 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
            className="text-xl text-gray-600"
          >
            Die kleinste Stadt Österreichs 🏰
          </motion.p>
        </div>

        {/* Fun Facts */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="bg-white rounded-xl shadow-lg p-6 mb-6"
        >
          <h2 className="text-2xl font-semibold mb-4 text-gray-800 flex items-center gap-2">
            <Sparkles className="w-6 h-6 text-yellow-500" />
            Wussten Sie schon?
          </h2>
          
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <div className="w-2 h-2 bg-amber-500 rounded-full mt-2"></div>
              <p className="text-gray-700">
                Mit nur <strong>0,11 km²</strong> Fläche ist Rattenberg die kleinste Gemeinde Tirols und ganz Österreichs!
              </p>
            </div>
            
            <div className="flex items-start gap-3">
              <div className="w-2 h-2 bg-amber-500 rounded-full mt-2"></div>
              <p className="text-gray-700">
                Die gesamte Altstadt steht unter Denkmalschutz - ein mittelalterliches Juwel!
              </p>
            </div>
            
            <div className="flex items-start gap-3">
              <div className="w-2 h-2 bg-amber-500 rounded-full mt-2"></div>
              <p className="text-gray-700">
                Berühmt für seine Glasbläserkunst und die wunderschönen Glasmanufakturen.
              </p>
            </div>
          </div>
        </motion.div>

        {/* Energy Message */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="bg-gradient-to-r from-green-50 to-blue-50 rounded-xl p-6 mb-6"
        >
          <div className="flex items-center gap-3 mb-3">
            <Lightbulb className="w-8 h-8 text-green-600" />
            <h3 className="text-xl font-semibold text-gray-800">Energie in Rattenberg</h3>
          </div>
          
          <p className="text-gray-700 mb-4">
            Als historische Altstadt setzt Rattenberg auf <strong>Bewahrung statt Bebauung</strong>. 
            Die denkmalgeschützten Gebäude und die kompakte Größe bedeuten: keine großen Energieanlagen, 
            aber dafür umso mehr Charme!
          </p>
          
          <div className="bg-white/50 rounded-lg p-4">
            <p className="text-sm text-gray-600 italic">
              💡 Fun Fact: Die gesamte Stadt könnte theoretisch von einer einzigen modernen Windkraftanlage 
              mit Strom versorgt werden!
            </p>
          </div>
        </motion.div>

        {/* Alternative Energy Sources */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
          className="bg-white rounded-xl shadow-lg p-6 mb-6"
        >
          <h3 className="text-xl font-semibold mb-4 text-gray-800 flex items-center gap-2">
            <Heart className="w-6 h-6 text-red-500" />
            Rattenbergs wahre Energiequellen
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-amber-50 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Users className="w-5 h-5 text-amber-600" />
                <h4 className="font-medium text-gray-800">Tourismusenergie</h4>
              </div>
              <p className="text-sm text-gray-600">
                Tausende Besucher jährlich bringen Leben in die Gassen
              </p>
            </div>
            
            <div className="bg-blue-50 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Sparkles className="w-5 h-5 text-blue-600" />
                <h4 className="font-medium text-gray-800">Handwerkskunst</h4>
              </div>
              <p className="text-sm text-gray-600">
                Glasbläser erzeugen funkelnde Kunstwerke mit jahrhundertealter Tradition
              </p>
            </div>
            
            <div className="bg-green-50 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Home className="w-5 h-5 text-green-600" />
                <h4 className="font-medium text-gray-800">Gemeinschaftsgeist</h4>
              </div>
              <p className="text-sm text-gray-600">
                In der kleinsten Stadt kennt jeder jeden - das schweißt zusammen
              </p>
            </div>
            
            <div className="bg-purple-50 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Castle className="w-5 h-5 text-purple-600" />
                <h4 className="font-medium text-gray-800">Historische Kraft</h4>
              </div>
              <p className="text-sm text-gray-600">
                600 Jahre Geschichte in jedem Pflasterstein
              </p>
            </div>
          </div>
        </motion.div>

        {/* Call to Action */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
          className="text-center"
        >
          <p className="text-gray-600 mb-4">
            Möchten Sie eine Gemeinde mit Energieanlagen erkunden?
          </p>
          <Button
            onClick={onBack}
            className="bg-amber-600 hover:bg-amber-700 text-white"
          >
            Zurück zur Suche
          </Button>
        </motion.div>

        {/* Easter Egg Signature */}
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1 }}
          className="text-center mt-8 text-sm text-gray-500"
        >
          <p>🥚 Sie haben das Rattenberg-Easter-Egg gefunden! 🏰</p>
        </motion.div>
      </div>
    </motion.div>
  );
}